import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  PerformanceMetric,
  PerformancePoint,
} from '@/lib/performance.types'
import { performanceMetricValue } from '@/lib/performanceFormat'
import type { RovingPerformancePoint } from './useRovingPerformancePoint.types'

// A gap is never a stop: only a point with a real value at the active metric is reachable.
function nonNullCandidates(
  points: PerformancePoint[],
  metric: PerformanceMetric
): PerformancePoint[] {
  return points.filter(
    (point) => performanceMetricValue(point, metric) !== null
  )
}

export function useRovingPerformancePoint(
  points: PerformancePoint[],
  metric: PerformanceMetric,
  onActivate: (runId: string) => void
): RovingPerformancePoint {
  const candidates = useMemo(
    () => nonNullCandidates(points, metric),
    [points, metric]
  )

  const [activeRunId, setActiveRunId] = useState<string | null>(
    candidates[0]?.runId ?? null
  )

  // Reconciles the active id whenever the previously active point drops out of the candidate set.
  useEffect(() => {
    if (candidates.some((point) => point.runId === activeRunId)) return
    setActiveRunId(candidates[0]?.runId ?? null)
  }, [candidates, activeRunId])

  const activeIndex = candidates.findIndex(
    (point) => point.runId === activeRunId
  )
  const activePoint = activeIndex >= 0 ? candidates[activeIndex] : null

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (candidates.length === 0) return

      const currentIndex = activeIndex >= 0 ? activeIndex : 0

      switch (event.key) {
        case 'ArrowRight': {
          event.preventDefault()
          setActiveRunId(
            candidates[Math.min(currentIndex + 1, candidates.length - 1)].runId
          )
          return
        }
        case 'ArrowLeft': {
          event.preventDefault()
          setActiveRunId(candidates[Math.max(currentIndex - 1, 0)].runId)
          return
        }
        case 'Home': {
          event.preventDefault()
          setActiveRunId(candidates[0].runId)
          return
        }
        case 'End': {
          event.preventDefault()
          setActiveRunId(candidates[candidates.length - 1].runId)
          return
        }
        case 'Enter': {
          if (!activePoint) return
          event.preventDefault()
          onActivate(activePoint.runId)
          return
        }
        default:
          return
      }
    },
    [candidates, activeIndex, activePoint, onActivate]
  )

  return { activePoint, onKeyDown, setActiveRunId }
}
